import { useRef, useState, useEffect, MutableRefObject } from 'react'
import {
  isFuture,
  add,
  addMilliseconds,
  sub,
  closestIndexTo,
  isAfter,
  isBefore,
  differenceInMilliseconds,
} from 'date-fns'

type Timer = Date | null

const subscribeNextSchedulingNotification = (
  notifications: Assignment[],
  threshold: number,
  timer: Timer,
  setTimer: (value: Timer) => void,
  timeoutID: MutableRefObject<NodeJS.Timeout | undefined>,
): void => {
  const futureNotifications = notifications.filter(notification =>
    isFuture(sub(notification.scheduling, { minutes: 5 })),
  )

  if (futureNotifications.length === 0) {
    return
  }

  const now = new Date()
  const closestNotificationIndex = closestIndexTo(
    now,
    futureNotifications.map(notification => notification.scheduling),
  ) as number

  const nextNotification = futureNotifications[closestNotificationIndex]
  const diff =
    differenceInMilliseconds(nextNotification.scheduling, now) -
    threshold * 60 * 1000

  if (
    timer &&
    differenceInMilliseconds(nextNotification.scheduling, timer) > 0
  ) {
    return
  }

  if (timeoutID.current) {
    clearTimeout(timeoutID.current)
  }

  timeoutID.current = setTimeout(() => {
    setTimer(addMilliseconds(new Date(), diff))
  }, diff)
}

function useSchedulingNotification(
  assignments: Assignment[],
  threshold = 5,
): Assignment[] {
  const [timer, setTimer] = useState<Timer>(null)
  const timeoutID = useRef<NodeJS.Timeout>()

  // [-48h, +5m]
  const next5Minutes = add(new Date(), { minutes: threshold })
  const last48Hours = sub(new Date(), { hours: 48 })

  const notifications = assignments.filter(
    ({ scheduling }) =>
      isAfter(scheduling, last48Hours) && isBefore(scheduling, next5Minutes),
  )

  useEffect(() => {
    return (): void => {
      if (timeoutID.current) {
        clearTimeout(timeoutID.current)
      }
    }
  }, [timer])

  subscribeNextSchedulingNotification(
    assignments,
    threshold,
    timer,
    setTimer,
    timeoutID,
  )

  return notifications
}

export { useSchedulingNotification }
